import { DocumentTypeCat } from '@ps/platform-message-framework/common/DocumentTypeCat';
import React, { useEffect, useMemo } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { Country as CountryEnum } from '@swf/core/enums';
import useFirstRender from '@swf/core/hooks/useFirstRender';
import { select } from '@swf/core/store';

import StateField from '../../../RMFAddress/components/Fields/components/StateField';
import SelectCitizenshipFlag from '../../../RMFCitizenship/components/SelectCitizenshipFlag/SelectCitizenshipFlag';
import DocumentDescriptionField from '../../../RMFDocumentation/components/DocumentDescriptionField/DocumentDescriptionField';
import DocumentTypeField from '../../../RMFDocumentation/components/DocumentTypeField/DocumentTypeField';
import SecondFamilyName from '../../../RMFLegalName/components/SecondFamilyName/SecondFamilyName';

import TaxCodeField, { TaxCodeType } from './components/TaxCodeField/TaxCodeField';

interface Props {
  formVisible: boolean;
}

const Fields: React.FC<Props> = ({ formVisible }) => {
  const signupCountry = useSelector(select.account.getCountry);
  const firstRender = useFirstRender();
  const form = useForm();

  const {
    input: { value: country }
  } = useField('country', { initialValue: signupCountry });

  const {
    input: { value: docType }
  } = useField('docType', { subscription: { value: true } });

  const {
    input: { value: citizenship }
  } = useField('citizenship', { subscription: { value: true }, initialValue: signupCountry });

  const isCountrySpain = country === CountryEnum.Spain;
  const isSpanishCitizen = citizenship === CountryEnum.Spain;

  const taxCodeType: TaxCodeType | null = useMemo(() => {
    switch (true) {
      case isCountrySpain && isSpanishCitizen:
        return TaxCodeType.DNI;
      case isCountrySpain && !isSpanishCitizen:
        return TaxCodeType.NIE;
      case !isCountrySpain && isSpanishCitizen:
        return TaxCodeType.DNI;
      case !isCountrySpain && !isSpanishCitizen:
        return TaxCodeType.NONE;
      default:
        return null;
    }
  }, [isCountrySpain, isSpanishCitizen]);

  useEffect(() => {
    if (firstRender) return;
    form.change('docOtherTypeName');
    form.change('nationalDocumentNumber');
    const fields = form.getRegisteredFields();

    if (fields.includes('nationalDocumentNumber')) {
      form.resetFieldState('nationalDocumentNumber');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citizenship, country]);

  return (
    <fieldset data-testid="identity-verification-form-fields">
      <SelectCitizenshipFlag />
      {isSpanishCitizen && <SecondFamilyName />}
      {isCountrySpain && <StateField country={country} fieldName="state" />}
      <DocumentTypeField country={country} citizenship={citizenship} />
      {taxCodeType && <TaxCodeField taxCodeType={taxCodeType} />}
      {!isCountrySpain && !isSpanishCitizen && docType === DocumentTypeCat.OTHER && (
        <DocumentDescriptionField />
      )}
    </fieldset>
  );
};

export default Fields;
